import React from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';

import { UrlParams } from 'common/url-params';
import { ListingHeader, ListingSidebar } from 'components/common';
import { ProductList } from 'components/product';
import { SearchPageSuggestedCategories } from 'components/search';
import {
  Breadcrumbs, Error, ImageMessage, Pagination, Skeleton
} from 'components/ui';
import { IMAGES } from 'constants/images';
import { APP_ROUTES } from 'constants/routes';
import { useSearchProducts } from 'hooks/data/useSearchProducts';
import { useUser } from 'hooks/data/useUser';
import { useAnalytics } from 'hooks/useAnalytics';
import { useAnalyticsViewItemList } from 'hooks/useAnalyticsViewItemList';

import type { PaginationOnChangeEvent } from 'components/ui';
import {
  type IProductsListing, type ISearchPageUrlParams
} from 'types';

import styles from './SearchTemplate.module.scss';

const BREADCRUMBS = [
  {
    href: APP_ROUTES.HOME,
    name: 'NAVIGATION.HOME',
    translate: true
  },
  {
    name: 'SEARCH.TITLE',
    translate: true
  }
];

interface Props {
  urlParams: ISearchPageUrlParams
}

const SearchTemplate = (props: Props) => {

  const { urlParams } = props;
  const { identifier: keyword } = urlParams;
  const {
    data: results, isError, isLoading, isSuccess
  } = useSearchProducts(keyword, urlParams);

  if (isError) {
    return (
      <>
        {/* Breadcrumbs */}
        <Breadcrumbs className={styles.breadcrumbs} items={BREADCRUMBS} />
        <Error />
      </>
    );
  }

  if (isLoading) {
    <>
      {/* Breadcrumbs */}
      <Breadcrumbs className={styles.breadcrumbs} items={BREADCRUMBS} />
      <Skeleton count={3} />
    </>;
  }

  if (isSuccess) {
    return (
      <SearchTemplateContent
        urlParams={urlParams}
        results={results}
        keyword={keyword || ''}
      />
    );
  }

  return null;
};

const SearchTemplateContent = (props: {results: IProductsListing, keyword: string, urlParams: ISearchPageUrlParams }) => {
  const { results, keyword, urlParams } = props;
  const {
    analyticsListId, analyticsListName,
    items: products,
    pages: totalPages, page: activePage, total: count,
    filters, orderBy,
  } = results || {};

  const { t } = useTranslation();
  const router = useRouter();
  const analytics = useAnalytics();
  const { data: user } = useUser({ isOptional: true });

  // Effects

  React.useEffect(() => {
    analytics.search({ user, term: keyword, results: count });
  }, [user, keyword, count]);

  // Analytics
  useAnalyticsViewItemList(products, { analyticsListId, analyticsListName });

  // Handlers

  const onPageChange = (e: PaginationOnChangeEvent) => {
    const { selected } = e;
    const { query: { slug: segments } } = router;
    const url = UrlParams.setPaginationParams(selected + 1, { segments, pageType: urlParams.pageType });
    router.push(url);
  };

  // Props

  const title = t(`SEARCH.NO_OF_RESULTS${count !== 1 ? '_PLURAL' : ''}`, { count, keyword });

  // Render

  return (
    <>

      {/* Breadcrumbs */}
      <Breadcrumbs className={styles.breadcrumbs} items={BREADCRUMBS} />

      {/* Container */}
      <div className={styles.container}>

        {/* Side */}
        <ListingSidebar
          count={count}
          filters={filters}
          pageType={urlParams.pageType}
          withFilters
          startSlot={() => <SearchPageSuggestedCategories keyword={keyword} />}
        />

        {/* Main */}
        <div className={styles.main}>

          {/* Header */}
          <ListingHeader
            title={title}
            products={products}
            count={count}
            pageType={urlParams.pageType}
            filters={filters}
            orderBy={orderBy}
            titleDataTestId="search-title"
          />

          {/* Products */}
          {
            count === 0
              ? (
                <NoProducts
                  keyword={keyword}
                />
              ) : (
                <>
                  {/* List */}
                  <ProductList
                    className={styles.list}
                    products={products}
                    dataTestId="search-products-list"
                    productListProps={{
                      analyticsListId,
                      analyticsListName
                    }}
                  />

                  {/* Pagination */}
                  {
                    products?.length > 0 && totalPages > 1 && (
                      <Pagination
                        onPageChange={onPageChange}
                        pageCount={totalPages}
                        activePage={activePage}
                      />
                    )
                  }
                </>
              )
          }

        </div>

      </div>
    </>
  );
};

const NoProducts = (props: {keyword: string}) => {
  const { keyword } = props;
  const { t } = useTranslation();

  return (
    <ImageMessage
      className={styles.emptyResults}
      image={IMAGES.SEARCH.NO_RESULTS}
      imageProps={{
        width: 414,
        height: 242,
        alt: 'No search results'
      }}
      title={t('SEARCH.NO_RESULTS_FOR', { keyword })}
      subtitle={t('SEARCH.NO_RESULTS_FOR_SUBTITLE')}
    />
  );

};

export default SearchTemplate;
